import React, { useState, useEffect, useRef } from 'react';
import { Plus, Minus, HelpCircle, MessageCircle, X, CheckCircle, User, Phone, Mail, MapPin, Map, FileText, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .iv *, .iv *::before, .iv *::after { font-family: 'Outfit', sans-serif; box-sizing: border-box; margin: 0; padding: 0; }
  .iv h1, .iv h2, .iv h3, .iv h4 { font-family: 'Cormorant Garamond', serif; }

  .iv {
    --navy:        #0A1535;
    --navy-mid:    #0D1D45;
    --blue:        #1B4FD8;
    --blue-mid:    #2563EB;
    --blue-bright: #3B7BFF;
    --gold:        #D4A017;
    --ice:         #F0F5FF;
    --ice-mid:     #DBEAFE;
    --border:      #E2E8F0;
    --text:        #1E293B;
    --muted:       #64748B;
    --white:       #FFFFFF;
    --green:       #10B981;
  }

  /* ── FAQ SECTION ── */
  .iv-faq { background: var(--ice); position: relative; overflow: hidden; padding: 112px 0; }
  .iv-dots { position: absolute; inset: 0; pointer-events: none; z-index: 0; background-image: radial-gradient(circle, rgba(37,99,235,0.12) 1px, transparent 1px); background-size: 32px 32px; }
  .iv-wash { position: absolute; top: -200px; left: 50%; transform: translateX(-50%); width: 900px; height: 500px; border-radius: 50%; background: radial-gradient(ellipse, rgba(59,123,255,0.06) 0%, transparent 70%); pointer-events: none; z-index: 0; }
  .iv-wash2 { position: absolute; bottom: -150px; right: -100px; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(212,160,23,0.05) 0%, transparent 70%); pointer-events: none; z-index: 0; }
  .iv-watermark { position: absolute; right: -40px; bottom: -60px; font-family: 'Cormorant Garamond', serif; font-size: 220px; font-weight: 600; font-style: italic; color: rgba(37,99,235,0.04); pointer-events: none; user-select: none; z-index: 0; letter-spacing: -0.04em; line-height: 1; }
  .iv-topline { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, var(--blue-bright) 35%, var(--gold) 70%, transparent); }
  .iv-inner { max-width: 1280px; margin: 0 auto; padding: 0 64px; position: relative; z-index: 2; }

  .iv-header { text-align: center; margin-bottom: 72px; }
  .iv-eyebrow { display: inline-flex; align-items: center; gap: 10px; padding: 8px 20px; margin-bottom: 24px; background: white; border: 1px solid var(--ice-mid); box-shadow: 0 2px 12px rgba(37,99,235,0.08); }
  .iv-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--blue-mid); animation: iv-blink 2s ease-in-out infinite; }
  @keyframes iv-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
  .iv-eyebrow-text { font-size: 10px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase; color: var(--blue-mid); }
  .iv-h2 { font-size: clamp(40px,5vw,66px); font-weight: 600; line-height: 1.04; letter-spacing: -0.02em; color: var(--navy); margin: 0 0 18px; }
  .iv-h2 em { font-style: italic; font-weight: 500; color: var(--blue-bright); }
  .iv-h2-sub { font-size: 16px; color: var(--muted); max-width: 460px; margin: 0 auto; line-height: 1.72; }
  .iv-rule { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 24px; }
  .iv-rule-line { flex: 0 0 56px; height: 1px; background: var(--border); }
  .iv-rule-diamond { width: 6px; height: 6px; background: var(--gold); transform: rotate(45deg); }

  .iv-body { display: grid; grid-template-columns: 260px 1fr; gap: 56px; align-items: start; }
  .iv-sidebar { position: sticky; top: 100px; display: flex; flex-direction: column; gap: 14px; }

  .iv-count-box { background: var(--navy); padding: 32px 24px; text-align: center; position: relative; overflow: hidden; box-shadow: 0 16px 48px rgba(10,21,53,0.16); }
  .iv-count-box::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background: linear-gradient(90deg, var(--blue-mid), var(--gold)); }
  .iv-count-shine { position:absolute; top:-40px; right:-40px; width:120px; height:120px; border-radius:50%; background:radial-gradient(circle, rgba(59,123,255,0.12) 0%, transparent 70%); }
  .iv-count-num { font-size: 68px; font-weight: 600; color: white; line-height: 1; letter-spacing: -0.02em; margin-bottom: 8px; }
  .iv-count-label { font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.45); letter-spacing: 0.18em; text-transform: uppercase; }

  .iv-info-box { background: white; padding: 22px 20px; border: 2px solid var(--border); border-left: 4px solid var(--gold); box-shadow: 0 4px 20px rgba(10,21,53,0.06); }
  .iv-info-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
  .iv-info-icon { width: 32px; height: 32px; background: var(--ice); border: 1px solid var(--ice-mid); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .iv-info-title { font-size: 13px; font-weight: 600; color: var(--navy); }
  .iv-info-text { font-size: 12.5px; line-height: 1.72; color: var(--muted); }
  .iv-info-text strong { color: var(--navy); font-weight: 600; }
  .iv-info-phone { display: flex; align-items: center; gap: 8px; margin-top: 14px; padding: 10px 14px; background: var(--ice); border: 1px solid var(--ice-mid); text-decoration: none; font-size: 14px; font-weight: 700; color: var(--blue-mid); letter-spacing: 0.02em; transition: background 0.2s ease, color 0.2s ease; }
  .iv-info-phone:hover { background: var(--blue-mid); color: white; }

  .iv-list { display: flex; flex-direction: column; gap: 10px; }
  .iv-item { background: white; border: 2px solid var(--border); overflow: hidden; opacity: 0; transform: translateY(14px); transition: opacity 0.55s ease, transform 0.55s ease, border-color 0.3s ease, box-shadow 0.3s ease; }
  .iv-item.vis { opacity: 1; transform: none; }
  .iv-item.open { border-color: var(--blue-mid); box-shadow: 0 8px 32px rgba(37,99,235,0.1); }

  .iv-q { width: 100%; display: flex; align-items: center; gap: 18px; padding: 20px 22px; text-align: left; background: none; border: none; cursor: pointer; transition: background 0.2s ease; }
  .iv-q:hover { background: var(--ice); }
  .iv-item.open .iv-q { background: var(--ice); }
  .iv-q-num { font-family: 'Cormorant Garamond', serif; font-size: 18px; font-weight: 500; min-width: 30px; color: var(--border); flex-shrink: 0; line-height: 1; transition: color 0.3s ease; }
  .iv-item.open .iv-q-num { color: var(--gold); }
  .iv-q-text { flex: 1; font-size: 14px; font-weight: 600; color: var(--navy); line-height: 1.45; text-align: left; transition: color 0.2s ease; }
  .iv-item.open .iv-q-text { color: var(--blue-mid); }
  .iv-q-btn { width: 34px; height: 34px; flex-shrink: 0; background: var(--ice); border: 2px solid var(--border); display: flex; align-items: center; justify-content: center; transition: background 0.3s ease, border-color 0.3s ease; }
  .iv-item.open .iv-q-btn { background: var(--blue-mid); border-color: var(--blue-mid); }

  .iv-ans { max-height: 0; overflow: hidden; transition: max-height 0.38s ease; }
  .iv-item.open .iv-ans { max-height: 400px; }
  .iv-ans-inner { padding: 16px 22px 22px 70px; border-top: 1px solid var(--border); font-size: 14px; line-height: 1.80; color: var(--muted); }
  .iv-ans-text { margin-bottom: 16px; }

  .iv-ctc-btn { display: inline-flex; align-items: center; gap: 8px; padding: 9px 18px; background: var(--blue-mid); color: white; font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.04em; border: none; cursor: pointer; transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease; box-shadow: 0 4px 14px rgba(37,99,235,0.25); }
  .iv-ctc-btn:hover { background: var(--blue); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,99,235,0.35); }
  .iv-ctc-btn:active { transform: translateY(0); }
  .iv-ctc-arrow { display: inline-block; transition: transform 0.2s ease; }
  .iv-ctc-btn:hover .iv-ctc-arrow { transform: translateX(3px); }

  .iv-item-prog { height: 0; background: linear-gradient(90deg, var(--blue-mid), var(--blue-bright)); transition: height 0.3s ease; }
  .iv-item.open .iv-item-prog { height: 3px; }
  .iv-fade { opacity: 0; transform: translateY(28px); transition: opacity 0.8s ease, transform 0.8s ease; }
  .iv-fade.on { opacity: 1; transform: none; }

  /* ── MODAL OVERLAY ── */
  .iv-overlay {
    position: fixed; inset: 0; z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    opacity: 0; pointer-events: none;
    transition: opacity 0.3s ease;
  }
  .iv-overlay.show { opacity: 1; pointer-events: all; }

  .iv-overlay-bg {
    position: absolute; inset: 0;
    background: rgba(10, 21, 53, 0.65);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }

  .iv-modal {
    position: relative; z-index: 2;
    background: white;
    width: 100%; max-width: 620px;
    max-height: 80vh; overflow-y: auto;
    box-shadow: 0 32px 80px rgba(10,21,53,0.35);
    transform: translateY(32px) scale(0.97);
    transition: transform 0.35s cubic-bezier(0.34,1.28,0.64,1);
    margin-top: 60px;
  }
  .iv-overlay.show .iv-modal { transform: translateY(0) scale(1); }

  .iv-modal-topbar {
    position: sticky; top: 0; z-index: 10;
    background: var(--navy); padding: 20px 28px;
    display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  }
  .iv-modal-topbar::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--blue-mid), var(--blue-bright), var(--gold));
  }

  .iv-modal-title-wrap { display: flex; flex-direction: column; gap: 4px; }
  .iv-modal-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase; color: var(--gold); }
  .iv-modal-title { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 600; color: white; line-height: 1.1; }
  .iv-modal-title em { font-style: italic; color: var(--blue-bright); font-weight: 500; }

  .iv-modal-close {
    width: 36px; height: 36px; flex-shrink: 0;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; margin-top: 2px;
    transition: background 0.2s ease;
  }
  .iv-modal-close:hover { background: rgba(255,255,255,0.18); }

  .iv-modal-body { padding: 32px 28px 28px; }

  .iv-modal-sub { font-size: 13.5px; color: var(--muted); line-height: 1.7; margin-bottom: 6px; }
  .iv-ctx-tag { display: inline-block; margin-bottom: 24px; padding: 5px 12px; background: var(--ice-mid); border: 1px solid var(--border); font-size: 11px; font-weight: 600; color: var(--blue-mid); letter-spacing: 0.05em; }

  .iv-divider { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
  .iv-divider-line { flex: 1; height: 1px; background: var(--border); }
  .iv-divider-diamond { width: 5px; height: 5px; background: var(--gold); transform: rotate(45deg); flex-shrink: 0; }

  .iv-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .iv-form-group { display: flex; flex-direction: column; gap: 6px; }
  .iv-form-group.full { grid-column: 1 / -1; }

  .iv-label { display: flex; align-items: center; gap: 6px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--navy); }
  .iv-label-req { color: var(--gold); font-size: 13px; line-height: 1; }
  .iv-label-opt { color: var(--muted); font-weight: 400; letter-spacing: 0; text-transform: none; font-size: 11px; margin-left: 2px; }

  .iv-input-wrap { position: relative; }
  .iv-input-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); pointer-events: none; }
  .iv-ta-icon { position: absolute; left: 13px; top: 14px; pointer-events: none; }

  .iv-input, .iv-select, .iv-textarea {
    width: 100%; padding: 12px 14px 12px 40px;
    border: 2px solid var(--border); background: var(--ice);
    font-family: 'Outfit', sans-serif; font-size: 13.5px; color: var(--text);
    outline: none; transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
    appearance: none; -webkit-appearance: none;
  }
  .iv-input:focus, .iv-select:focus, .iv-textarea:focus {
    border-color: var(--blue-mid); background: white;
    box-shadow: 0 0 0 4px rgba(37,99,235,0.08);
  }
  .iv-input::placeholder, .iv-textarea::placeholder { color: #B0BECC; font-size: 13px; }
  .iv-select { cursor: pointer; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748B' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 13px center; padding-right: 36px; }
  .iv-textarea { resize: vertical; min-height: 96px; line-height: 1.65; padding-top: 12px; }
  .iv-input.err, .iv-select.err, .iv-textarea.err { border-color: #EF4444; background: #FFF5F5; }

  .iv-err-msg { font-size: 11px; color: #EF4444; margin-top: 1px; }

  .iv-modal-footer { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .iv-modal-note { font-size: 11.5px; color: var(--muted); line-height: 1.6; max-width: 280px; }
  .iv-modal-note strong { color: var(--navy); }

  .iv-submit {
    display: inline-flex; align-items: center; gap: 9px;
    padding: 13px 28px; background: var(--navy); color: white;
    border: none; cursor: pointer; font-family: 'Outfit', sans-serif;
    font-size: 13px; font-weight: 700; letter-spacing: 0.06em;
    position: relative; overflow: hidden;
    transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    box-shadow: 0 6px 20px rgba(10,21,53,0.18); flex-shrink: 0;
  }
  .iv-submit::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background: linear-gradient(90deg, var(--blue-mid), var(--gold)); }
  .iv-submit:hover { background: var(--blue-mid); transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,0.28); }
  .iv-submit:active { transform: translateY(0); }
  .iv-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

  /* ── SUCCESS STATE ── */
  .iv-success {
    padding: 48px 28px 40px; text-align: center;
    display: flex; flex-direction: column; align-items: center; gap: 16px;
    animation: iv-pop 0.45s cubic-bezier(0.34,1.5,0.64,1);
  }
  @keyframes iv-pop { from{opacity:0;transform:scale(0.82)} to{opacity:1;transform:scale(1)} }
  .iv-success-ring { width: 76px; height: 76px; border-radius: 50%; background: rgba(16,185,129,0.08); border: 2px solid rgba(16,185,129,0.2); display: flex; align-items: center; justify-content: center; }
  .iv-success-title { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 600; color: var(--navy); }
  .iv-success-sub { font-size: 14px; color: var(--muted); max-width: 360px; line-height: 1.72; }
  .iv-success-close { margin-top: 8px; display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; background: var(--blue-mid); color: white; border: none; cursor: pointer; font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 600; transition: background 0.2s ease; }
  .iv-success-close:hover { background: var(--blue); }

  @media (max-width: 1024px) {
    .iv-body { grid-template-columns: 1fr; }
    .iv-inner { padding: 0 28px; }
    .iv-sidebar { position: static; flex-direction: row; }
    .iv-count-box, .iv-info-box { flex: 1; }
  }
  @media (max-width: 640px) {
    .iv-faq { padding: 80px 0; }
    .iv-sidebar { flex-direction: column; }
    .iv-ans-inner { padding-left: 22px; }
    .iv-watermark { display: none; }
    .iv-form-grid { grid-template-columns: 1fr; }
    .iv-form-group.full { grid-column: 1; }
    .iv-modal-footer { flex-direction: column; align-items: stretch; }
    .iv-submit { justify-content: center; }
    .iv-overlay { padding: 12px; align-items: flex-end; }
    .iv-modal { max-height: 85vh; }
  }
`;

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
  'Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman & Nicobar Islands','Chandigarh','Dadra & Nagar Haveli','Daman & Diu',
  'Delhi','Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const faqs = [
  { q: 'Why should I connect with IndusViva?', a: 'Connecting with IndusViva gives you access to a platform where wellness, growth, and opportunity come together. It enables you to build skills, create income through quality products, and grow at your own pace with a strong and supportive system.', cta: 'Take your first step towards growth today' },
  { q: 'What is the purpose of the IndusViva platform?', a: 'The platform is designed to help individuals create long-term value through a product-based model supported by structured learning, mentorship, and a clear path for growth.', cta: 'Build something meaningful with the right system' },
  { q: 'How does IndusViva create value for individuals?', a: 'IndusViva focuses on value through real product usage, skill development, and relationship building. This approach supports steady and meaningful progress rather than short-term gains.', cta: 'Start creating value that grows with you' },
  { q: 'How does the business model support long-term growth?', a: 'The model encourages consistent effort, guided mentorship, and team development, helping individuals grow step by step with stability and long-term direction.', cta: 'Grow steadily with a structured approach' },
  { q: 'What kind of training and mentorship is provided?', a: 'A structured "I Do – We Do – You Do" approach is followed, where individuals learn through guidance, collaboration, and hands-on experience before building independently.', cta: 'Learn, apply, and grow with confidence' },
  { q: 'What makes IndusViva different from other opportunities?', a: 'IndusViva stands out with its focus on product value, structured systems, and a supportive ecosystem that encourages sustainable and long-term growth.', cta: 'Be part of a system designed for real progress' },
  { q: 'How does IndusViva ensure compliant operations?', a: 'The company follows established direct selling guidelines, maintains transparent processes, and promotes responsible business practices across all levels.', cta: 'Move forward with clarity and confidence' },
  { q: 'What role do products play in the business?', a: 'Products are central to the model, ensuring that growth is driven by genuine usage, customer value, and consistent demand.', cta: 'Build on a foundation of real value' },
  { q: 'Can I start without prior experience?', a: 'Yes. The platform is designed for individuals from all backgrounds, with continuous training, support, and guidance to help them grow confidently.', cta: 'Start your journey, no experience required' },
  { q: 'How does IndusViva promote sustainability and responsibility?', a: 'Through structured programs and initiatives that encourage informed decisions, balanced growth, and a long-term approach to business and wellness.', cta: 'Grow responsibly with a long-term vision' },
];

/* ── MODAL FORM ── */
const Modal = ({ context, onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', state: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit number';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state) e.state = 'Select your state';
    return e;
  };

 const handleSubmit = async () => {
  const e = validate();
  if (Object.keys(e).length) {
    setErrors(e);
    return;
  }

  try {
    setLoading(true);

    // ── 1. Google Sheets ──
    await fetch('https://script.google.com/macros/s/AKfycbwSC_2kn4hW95Xa2yL8vLi4BWFFy7ge0EJpA8S1MYqjm-5NnP--cIK8gqoVvYNSEM3Q/exec', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });

    const emailData = {
      user_name:    form.name,
      user_phone:   form.phone,
      user_email:   form.email,
      user_city:    form.city,
      user_state:   form.state,
      user_message: form.message || 'No message provided',
      context:      context,
    };

    // ── 2. Owner Notification ──
    const ownerRes = await emailjs.send(
      'service_e3jwmmm',
      'template_kyggntk',
      emailData,
      'uMxrXzGkQtl0pbeRp'
    );

    // ── 3. Customer Thank You ──
    const customerRes = await emailjs.send(
      'service_e3jwmmm',
      'template_6chdg0n',
      emailData,
      'uMxrXzGkQtl0pbeRp'
    );

    setSubmitted(true);

  } catch (err) {
    console.error(err);
    alert("Something went wrong");
  } finally {
    setLoading(false);
  }
};

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <>
      {/* Dark overlay — click to close */}
      <div className="iv-overlay-bg" onClick={onClose} />

      <div className="iv-modal">
        {/* Top bar */}
        <div className="iv-modal-topbar">
          <div className="iv-modal-title-wrap">
            <span className="iv-modal-eyebrow">Join VibrantViva</span>
            <h3 className="iv-modal-title">Connect &amp; <em>Begin</em> Your Journey</h3>
          </div>
          <button className="iv-modal-close" onClick={onClose}><X size={16} color="white" /></button>
        </div>

        {submitted ? (
          <div className="iv-success">
            <div className="iv-success-ring"><CheckCircle size={36} color="#10B981" /></div>
            <h3 className="iv-success-title">You're Connected!</h3>
            <p className="iv-success-sub">
              Thank you, <strong>{form.name}</strong>. Welcome to our Team. Your VibrantViva journey begins now.
            </p>
            <button className="iv-success-close" onClick={onClose}>Close &amp; Continue →</button>
          </div>
        ) : (
          <div className="iv-modal-body">
            <p className="iv-modal-sub">Fill in your details and our team will guide you through the next steps.</p>
            {context && <div className="iv-ctx-tag">📌 {context}</div>}

            <div className="iv-divider">
              <div className="iv-divider-line" />
              <div className="iv-divider-diamond" />
              <div className="iv-divider-line" />
            </div>

            <div className="iv-form-grid">

              {/* Full Name */}
              <div className="iv-form-group">
                <label className="iv-label"><User size={11} /> Full Name <span className="iv-label-req">*</span></label>
                <div className="iv-input-wrap">
                  <User size={14} color="#94A3B8" className="iv-input-icon" />
                  <input className={`iv-input${errors.name ? ' err' : ''}`} placeholder="e.g. Ravi Kumar" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                {errors.name && <span className="iv-err-msg">{errors.name}</span>}
              </div>

              {/* Phone */}
              <div className="iv-form-group">
                <label className="iv-label"><Phone size={11} /> Phone Number <span className="iv-label-req">*</span></label>
                <div className="iv-input-wrap">
                  <Phone size={14} color="#94A3B8" className="iv-input-icon" />
                  <input className={`iv-input${errors.phone ? ' err' : ''}`} placeholder="10-digit mobile" value={form.phone} onChange={e => set('phone', e.target.value.replace(/\D/g,'').slice(0,10))} />
                </div>
                {errors.phone && <span className="iv-err-msg">{errors.phone}</span>}
              </div>

              {/* Email */}
              <div className="iv-form-group">
                <label className="iv-label"><Mail size={11} /> Email Address <span className="iv-label-req">*</span></label>
                <div className="iv-input-wrap">
                  <Mail size={14} color="#94A3B8" className="iv-input-icon" />
                  <input className={`iv-input${errors.email ? ' err' : ''}`} placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
                {errors.email && <span className="iv-err-msg">{errors.email}</span>}
              </div>

              {/* City */}
              <div className="iv-form-group">
                <label className="iv-label"><MapPin size={11} /> City <span className="iv-label-req">*</span></label>
                <div className="iv-input-wrap">
                  <MapPin size={14} color="#94A3B8" className="iv-input-icon" />
                  <input className={`iv-input${errors.city ? ' err' : ''}`} placeholder="e.g. Bengaluru" value={form.city} onChange={e => set('city', e.target.value)} />
                </div>
                {errors.city && <span className="iv-err-msg">{errors.city}</span>}
              </div>

              {/* State */}
              <div className="iv-form-group full">
                <label className="iv-label"><Map size={11} /> State <span className="iv-label-req">*</span></label>
                <div className="iv-input-wrap">
                  <Map size={14} color="#94A3B8" className="iv-input-icon" />
                  <select className={`iv-select${errors.state ? ' err' : ''}`} value={form.state} onChange={e => set('state', e.target.value)}>
                    <option value="">Select your state</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {errors.state && <span className="iv-err-msg">{errors.state}</span>}
              </div>

              {/* Message */}
              <div className="iv-form-group full">
                <label className="iv-label"><FileText size={11} /> Message / Query <span className="iv-label-opt">(Optional)</span></label>
                <div className="iv-input-wrap">
                  <FileText size={14} color="#94A3B8" className="iv-ta-icon" />
                  <textarea className="iv-textarea" style={{paddingLeft:'40px'}} placeholder="Tell us about yourself or what you'd like to know…" value={form.message} onChange={e => set('message', e.target.value)} />
                </div>
              </div>

            </div>

            <div className="iv-modal-footer">
              <p className="iv-modal-note">Your details are <strong>100% confidential</strong> and never shared with third parties.</p>
              <button className="iv-submit" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Submitting…' : <><Send size={14} /> Submit &amp; Connect</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ── MAIN ── */
const FAQ = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [open, setOpen] = useState(0);
  const [formCtx, setFormCtx] = useState(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = formCtx !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [formCtx]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="iv" ref={ref}>

        {/* ── FAQ SECTION ── */}
        <section className="iv-faq">
          <div className="iv-dots" />
          <div className="iv-wash" />
          <div className="iv-wash2" />
          <div className="iv-watermark">FAQ</div>
          <div className="iv-topline" />

          <div className="iv-inner">
            <div className={`iv-header iv-fade ${vis ? 'on' : ''}`}>
              <div className="iv-eyebrow">
                <div className="iv-eyebrow-dot" />
                <HelpCircle size={11} color="#2563EB" />
                <span className="iv-eyebrow-text">Frequently Asked Questions</span>
              </div>
              <h2 className="iv-h2">Got <em>Questions?</em><br />We've Got Answers.</h2>
              <p className="iv-h2-sub">Everything you need to know about IndusViva and our ethical, value-driven business approach.</p>
              <div className="iv-rule">
                <div className="iv-rule-line" /><div className="iv-rule-diamond" /><div className="iv-rule-line" />
              </div>
            </div>

            <div className={`iv-body iv-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '180ms' }}>
              <div className="iv-sidebar">
                <div className="iv-count-box">
                  <div className="iv-count-shine" />
                  <div className="iv-count-num">{faqs.length}</div>
                  <div className="iv-count-label">Common Questions</div>
                </div>
                <div className="iv-info-box">
                  <div className="iv-info-head">
                    <div className="iv-info-icon"><MessageCircle size={15} color="#2563EB" /></div>
                    <span className="iv-info-title">Need More Help?</span>
                  </div>
                  <p className="iv-info-text">Browse our most asked questions, or reach out to our team for <strong>personalized support</strong> and guidance.</p>
                  <a href="tel:18001034916" className="iv-info-phone">
                    <span>📞</span><span>1800 103 4916</span>
                  </a>
                </div>
              </div>

              <div className="iv-list">
                {faqs.map((faq, i) => {
                  const isOpen = open === i;
                  return (
                    <div key={i} className={`iv-item ${isOpen ? 'open' : ''} ${vis ? 'vis' : ''}`} style={{ transitionDelay: `${180 + i * 60}ms` }}>
                      <button className="iv-q" onClick={() => setOpen(isOpen ? -1 : i)}>
                        <span className="iv-q-num">{String(i + 1).padStart(2, '0')}</span>
                        <span className="iv-q-text">{faq.q}</span>
                        <div className="iv-q-btn">
                          {isOpen ? <Minus size={14} color="white" /> : <Plus size={14} color="#2563EB" />}
                        </div>
                      </button>
                      <div className="iv-ans">
                        <div className="iv-ans-inner">
                          <p className="iv-ans-text">{faq.a}</p>
                          <button className="iv-ctc-btn" onClick={() => setFormCtx(faq.cta)}>
                            {faq.cta} <span className="iv-ctc-arrow">→</span>
                          </button>
                        </div>
                      </div>
                      <div className="iv-item-prog" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ── MODAL ── */}
        <div className={`iv-overlay ${formCtx !== null ? 'show' : ''}`}>
          {formCtx !== null && <Modal context={formCtx} onClose={() => setFormCtx(null)} />}
        </div>

      </div>
    </>
  );
};

export default FAQ;